'use client';

import { useMemo } from 'react';
import { CheckCheck, Lock, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatDateForUser } from '@/utils/formatters';
import { timeToMinutes } from '@/utils/time';
import {
  formatKnockoutStart,
  getFinalDay,
  isKnockoutSlot,
  type KnockoutStart,
} from '@/utils/tournament-schedule';
import { dayOfWeekMap } from '@/components/clubs/upsert-club/validations/validations';
import type { Availability, TimeRange } from '@/types/tournament.model';
import type { FinalDayRule } from './final-day-rule';

interface AvailabilityPickerProps {
  /** Calendario completo del torneo (cada día con todos sus timeRange disponibles). */
  schedule: Availability[];
  /** Mapa date → set de startTimes seleccionados (manejado por el padre). */
  selectedByDate: Record<string, Set<string>>;
  onChange: (next: Record<string, Set<string>>) => void;
  /** What the last day is for: drives the note about it and its label. */
  finalDayRule?: FinalDayRule;
  /** Knockout cut: slots from it onwards are locked in. Without it, the last day is. */
  knockoutStart?: KnockoutStart | null;
  /**
   * Replaces the helper text under the title (the default one describes a new registration).
   * The note about the last day always follows it.
   */
  description?: string;
}

const FINAL_DAY_NOTE: Record<FinalDayRule, string> = {
  crossovers:
    'El último día está reservado para los cruces y se incluye siempre completo.',
  knockoutEveryDay: 'El último día se incluye siempre completo.',
  knockoutLastDayOnly:
    'El cuadro se juega solo el último día, que se incluye siempre completo.',
};

/** Note about the locked slots when the tournament has a knockout cut. */
const knockoutStartNote = (
  finalDayRule: FinalDayRule,
  knockoutStart: KnockoutStart,
): string =>
  finalDayRule === 'knockoutLastDayOnly'
    ? `El cuadro se juega desde el ${formatKnockoutStart(knockoutStart, true)}, y esos horarios se incluyen siempre completos.`
    : `Desde el ${formatKnockoutStart(knockoutStart, true)} se juegan las llaves: esos horarios se incluyen siempre completos.`;

/**
 * Ticks every slot of `days` in the picker: the whole calendar for a new registration (the manager
 * unticks what does not apply), or what the team already declared when editing one.
 *
 * Keyed by startTime like the picker, so a declared slot that no longer exists in the tournament
 * schedule simply has no button to light up and is dropped on save. Entries sharing a date are
 * merged: a registration can hold more than one row per day, and keeping the last one would drop
 * the others on save.
 */
export function toSelection(days: Availability[]): Record<string, Set<string>> {
  const result: Record<string, Set<string>> = {};
  days.forEach((day) => {
    const selected = result[day.date] ?? new Set<string>();
    (day.timeRange ?? []).forEach((slot) => selected.add(slot.startTime));
    result[day.date] = selected;
  });
  return result;
}

/**
 * Whether the calendar has slots to pick before the final day. Without them (a single-day
 * tournament, or earlier days declared with no hours) the final day is all there is, and it always
 * goes in whole, so there is nothing to choose.
 */
export function hasGroupDaySlots(
  schedule: Availability[],
  knockoutStart: KnockoutStart | null = null,
): boolean {
  const finalDay = getFinalDay(schedule);
  return schedule.some((d) =>
    (d.timeRange ?? []).some(
      (slot) => !isKnockoutSlot(d.date, slot.startTime, finalDay, knockoutStart),
    ),
  );
}

/**
 * Why an availability can't be saved, or null when it can.
 *
 * The final day is added on its own (see buildAvailabilityFromSelection), so an empty availability
 * is not the thing to guard against: a pair with nothing but the final day has no slot for the
 * group stage, or for the early rounds of a bracket that uses every day. The backend does not
 * check this for the manager (it skips the minimum of slots), so this is the only guard. Only a
 * bracket kept on the last day can do with that day alone.
 */
export function getMissingAvailabilityMessage(
  schedule: Availability[],
  availability: Availability[],
  finalDayRule: FinalDayRule = 'crossovers',
  knockoutStart: KnockoutStart | null = null,
): string | null {
  if (schedule.length === 0) return null;
  const finalDay = getFinalDay(schedule);
  if (
    hasGroupDaySlots(schedule, knockoutStart) &&
    finalDayRule !== 'knockoutLastDayOnly'
  ) {
    if (knockoutStart) {
      return hasGroupDaySlots(availability, knockoutStart)
        ? null
        : 'Elegí al menos un turno antes del corte de llaves.';
    }
    if (availability.some((d) => d.date !== finalDay)) return null;
    return finalDayRule === 'crossovers'
      ? 'Elegí al menos un turno en los días previos al de cruces.'
      : 'Elegí al menos un turno antes del último día.';
  }
  return availability.length > 0
    ? null
    : 'Elegí al menos un turno disponible para la pareja.';
}

/**
 * A team slot is just an hour. The schedule's `qty` counts courts, but on a team slot the generator
 * reads `qty` as "still free" (1) or "already playing" (0): a match takes it down by one. A slot
 * copied with qty=3 stays free after a match, so the pair can get a second match at the same hour
 * and the 2-per-day cap stops counting it. Same as the app, which sends start/end only and lets
 * the backend default qty to 1 (resetFixture puts team slots back to 1 too).
 */
const toTeamSlot = (slot: TimeRange): TimeRange => ({
  startTime: slot.startTime,
  endTime: slot.endTime,
  qty: 1,
  courtsQuantity: 1,
});

/**
 * The last day of the calendar always goes in whole, whatever was ticked.
 *
 * Same rule the player app applies when registering (`buildAvailability` in
 * `use-tournament-availability.tsx`), and it is not cosmetic: the generator infers a team's
 * knockout day from the highest date that team declared (`getLastDayAvailability`) and drops that
 * date from the group stage (`getTeamGroupAvailability`). A pair registered without the final day
 * drags its own "last day" one day back and loses that day for the groups.
 *
 * Slots are copied as hours only (see toTeamSlot).
 */
export function buildAvailabilityFromSelection(
  schedule: Availability[],
  selectedByDate: Record<string, Set<string>>,
  knockoutStart: KnockoutStart | null = null,
): Availability[] {
  const finalDay = getFinalDay(schedule);

  return schedule
    .map((day): Availability | null => {
      const selected = selectedByDate[day.date];
      const timeRange = (day.timeRange ?? [])
        .filter(
          (slot) =>
            isKnockoutSlot(day.date, slot.startTime, finalDay, knockoutStart) ||
            selected?.has(slot.startTime),
        )
        .map(toTeamSlot);
      if (timeRange.length === 0) return null;
      return { date: day.date, dayOfWeek: day.dayOfWeek, timeRange };
    })
    .filter((d): d is Availability => d !== null);
}

export function AvailabilityPicker({
  schedule,
  selectedByDate,
  onChange,
  finalDayRule = 'crossovers',
  knockoutStart = null,
  description = 'Marcá los turnos en los que la pareja puede jugar. Por default quedan todos seleccionados.',
}: AvailabilityPickerProps) {
  const sortedSchedule = useMemo(
    () =>
      [...schedule].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      ),
    [schedule],
  );

  // the knockout day is not negotiable — see buildAvailabilityFromSelection
  const finalDay = useMemo(() => getFinalDay(schedule), [schedule]);

  const isLocked = (date: string, startTime: string) =>
    isKnockoutSlot(date, startTime, finalDay, knockoutStart);

  const toggleSlot = (date: string, startTime: string) => {
    if (isLocked(date, startTime)) return;
    const current = new Set(selectedByDate[date] ?? []);
    if (current.has(startTime)) {
      current.delete(startTime);
    } else {
      current.add(startTime);
    }
    onChange({ ...selectedByDate, [date]: current });
  };

  const setAllForDay = (date: string, slots: TimeRange[], on: boolean) => {
    // locked slots stay selected whatever the button does
    const next = new Set<string>(
      slots
        .filter((s) => on || isLocked(date, s.startTime))
        .map((s) => s.startTime),
    );
    onChange({ ...selectedByDate, [date]: next });
  };

  const setAllForTournament = (on: boolean) => {
    const next: Record<string, Set<string>> = {};
    sortedSchedule.forEach((day) => {
      next[day.date] = new Set(
        (day.timeRange ?? [])
          .filter((s) => on || isLocked(day.date, s.startTime))
          .map((s) => s.startTime),
      );
    });
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h4 className="text-sm font-semibold">Disponibilidad de la pareja</h4>
          <p className="text-xs text-muted-foreground">
            {description}{' '}
            {knockoutStart
              ? knockoutStartNote(finalDayRule, knockoutStart)
              : FINAL_DAY_NOTE[finalDayRule]}
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <span className="text-xs text-muted-foreground">Seleccionar:</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAllForTournament(true)}
          >
            <CheckCheck className="h-3.5 w-3.5" />
            Todos
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAllForTournament(false)}
          >
            <Minus className="h-3.5 w-3.5" />
            Ninguno
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {sortedSchedule.map((day) => {
          const slots = [...(day.timeRange ?? [])].sort(
            (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime),
          );
          const lockedCount = slots.filter((s) =>
            isLocked(day.date, s.startTime),
          ).length;
          // without a cut the last day is locked even if it declared no hours
          const allLocked = knockoutStart
            ? slots.length > 0 && lockedCount === slots.length
            : day.date === finalDay;
          const someLocked = lockedCount > 0 && !allLocked;
          const selected = selectedByDate[day.date] ?? new Set<string>();
          const allOn =
            slots.length > 0 &&
            slots.every(
              (s) => isLocked(day.date, s.startTime) || selected.has(s.startTime),
            );
          const noneOn = slots.every(
            (s) => isLocked(day.date, s.startTime) || !selected.has(s.startTime),
          );

          return (
            <div
              key={day.date}
              className="rounded-lg border border-border p-3"
            >
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium">
                  {formatDateForUser(day.date)}{' '}
                  <span className="font-normal text-muted-foreground">
                    ({dayOfWeekMap[day.dayOfWeek] ?? day.dayOfWeek})
                  </span>
                </div>
                {allLocked ? (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Lock className="h-3.5 w-3.5" />
                    {knockoutStart
                      ? 'Llaves — va completo'
                      : finalDayRule === 'crossovers'
                        ? 'Día de cruces — va completo'
                        : 'Último día — va completo'}
                  </span>
                ) : (
                  <div className={cn('flex items-center gap-1', someLocked && 'flex-wrap')}>
                    {someLocked && knockoutStart && (
                      <span className="mr-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Lock className="h-3.5 w-3.5" />
                        {`Llaves desde las ${knockoutStart.startTime} — van completos`}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">Seleccionar:</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={allOn}
                      onClick={() => setAllForDay(day.date, slots, true)}
                    >
                      Todo el día
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={noneOn}
                      onClick={() => setAllForDay(day.date, slots, false)}
                    >
                      Ninguno
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                {slots.map((slot) => {
                  const locked = isLocked(day.date, slot.startTime);
                  const isSelected = locked || selected.has(slot.startTime);
                  return (
                    <Button
                      key={`${day.date}-${slot.startTime}`}
                      type="button"
                      variant={isSelected ? 'default' : 'outline'}
                      size="sm"
                      disabled={locked}
                      onClick={() => toggleSlot(day.date, slot.startTime)}
                      className="justify-center"
                    >
                      {slot.startTime} – {slot.endTime}
                    </Button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
